import React, { useState, useEffect } from 'react';
import { Star, Clock, CheckCircle, User, Calendar, MessageSquare, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';

export default function PendingReviews({ onReviewSubmit }) {
  const [pendingReviews, setPendingReviews] = useState([]);
  const [completedReviews, setCompletedReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending'); // 'pending', 'completed', 'all'

  useEffect(() => {
    fetchPendingReviews();
    fetchCompletedReviews();
  }, []);

  const fetchPendingReviews = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/employments/pending-reviews`, {
        withCredentials: true
      });
      setPendingReviews(response.data);
    } catch (error) {
      console.error('Failed to fetch pending reviews');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompletedReviews = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/reviews/my-reviews`, {
        withCredentials: true
      });
      setCompletedReviews(response.data);
    } catch (error) {
      console.error('Failed to fetch completed reviews');
    }
  };

  const handleWriteReview = (tradespersonId) => {
    if (onReviewSubmit) {
      onReviewSubmit(tradespersonId);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse bg-slate-100 h-24 rounded-lg"></div>
        ))}
      </div>
    );
  }

  const displayItems = filter === 'pending' 
    ? pendingReviews 
    : filter === 'completed' 
    ? completedReviews 
    : [...pendingReviews, ...completedReviews];

  return (
    <div className="space-y-6" data-testid="pending-reviews">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
          <Star className="h-5 w-5 text-orange-500" />
          Reviews
        </h3>
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('pending')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
              filter === 'pending'
                ? 'bg-orange-500 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
            data-testid="filter-pending"
          >
            Pending ({pendingReviews.length})
          </button>
          <button
            onClick={() => setFilter('completed')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
              filter === 'completed'
                ? 'bg-orange-500 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
            data-testid="filter-completed"
          >
            Completed ({completedReviews.length})
          </button>
        </div>
      </div>

      {/* Pending Reviews Alert */}
      {filter === 'pending' && pendingReviews.length > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-orange-800 font-medium">You have {pendingReviews.length} pending review{pendingReviews.length > 1 ? 's' : ''}</p>
            <p className="text-orange-600 text-sm">Help other employers by sharing your experience with these tradespeople.</p>
          </div>
        </div>
      )}

      {/* Reviews List */}
      {filter === 'pending' && (
        <div className="space-y-4">
          {pendingReviews.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 rounded-lg">
              <CheckCircle className="h-12 w-12 text-green-400 mx-auto mb-3" />
              <p className="text-slate-600 font-medium">All caught up!</p>
              <p className="text-slate-500 text-sm">No pending reviews at the moment.</p>
            </div>
          ) : (
            pendingReviews.map((item) => (
              <div
                key={item.employment_id}
                className="bg-white border border-slate-200 rounded-lg p-4 hover:border-orange-300 transition-all"
                data-testid={`pending-review-${item.employment_id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center">
                      <User className="h-6 w-6 text-slate-400" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">{item.tradesperson_name}</h4>
                      <p className="text-sm text-slate-500">{item.trade || 'Tradesperson'}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(item.start_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                          {' - '}
                          {new Date(item.end_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {item.duration_weeks < 1 
                            ? `${Math.round(item.duration_weeks * 7)} days` 
                            : `${item.duration_weeks} week${item.duration_weeks > 1 ? 's' : ''}`}
                        </span>
                      </div>
                      {item.notes && (
                        <p className="text-sm text-slate-600 mt-2 bg-slate-50 p-2 rounded">
                          {item.notes}
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleWriteReview(item.tradesperson_id)}
                    className="btn-primary text-sm flex items-center gap-2"
                    data-testid={`write-review-${item.employment_id}`}
                  >
                    <Star className="h-4 w-4" />
                    Write Review
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Completed Reviews */}
      {filter === 'completed' && (
        <div className="space-y-4">
          {completedReviews.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 rounded-lg">
              <MessageSquare className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600 font-medium">No reviews yet</p>
              <p className="text-slate-500 text-sm">Your submitted reviews will appear here.</p>
            </div>
          ) : (
            completedReviews.map((review) => (
              <div
                key={review.review_id}
                className="bg-white border border-slate-200 rounded-lg p-4"
                data-testid={`completed-review-${review.review_id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center">
                      <CheckCircle className="h-6 w-6 text-green-500" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">{review.tradesperson_name}</h4>
                      <div className="flex items-center gap-1 mt-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`h-4 w-4 ${
                              star <= review.overall_rating
                                ? 'text-yellow-400 fill-yellow-400'
                                : 'text-slate-200'
                            }`}
                          />
                        ))}
                        <span className="text-sm text-slate-600 ml-2">
                          {review.overall_rating?.toFixed(1)}
                        </span>
                      </div>
                      {review.comment && (
                        <p className="text-sm text-slate-600 mt-2 italic">"{review.comment}"</p>
                      )}
                      <p className="text-xs text-slate-400 mt-2">
                        Reviewed on {new Date(review.created_at).toLocaleDateString('en-GB', { 
                          day: 'numeric', 
                          month: 'short', 
                          year: 'numeric' 
                        })}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-slate-500 space-y-1">
                      <p>Punctuality: {review.punctuality}/5</p>
                      <p>Professionalism: {review.professionalism}/5</p>
                      <p>Quality: {review.quality}/5</p>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
